import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useMemo, useState } from "react";
import { ArrowBack } from "@mui/icons-material";
import { AppBar, Container, IconButton, List, ListItem, ListItemButton, ListItemText, Tab, Tabs, Toolbar, Typography } from "@mui/material";
import { bgSx, ScrollableFilledBox } from "./CalculatorStyled";
import { msgs } from "./CalculatorL10n";
const VARIABLES = {
    "π": 24 /* S.c_var_description_pi */,
    "e": 25 /* S.c_var_description_e */,
    "i": 26 /* S.c_var_description_i */,
    "c": 27 /* S.c_var_description_c */,
    "G": 28 /* S.c_var_description_G */,
    "h": 29 /* S.c_var_description_h_reduced */,
};
const MathPI = 3.14159265358979323846;
const VARIABLE_VALUES = {
    "π": MathPI,
    "e": 2.7182818284590452354,
    "i": "√(-1)",
    "c": 299792458,
    "G": "6.673848E-11",
    "h": String(6.6260695729E-34 / (2 * MathPI)).replace("e", "E"),
};
const FUNCTION_DESC = {
    "sin": 31 /* S.c_fun_description_sin */,
    "cos": 32 /* S.c_fun_description_cos */,
    "tan": 33 /* S.c_fun_description_tan */,
    "cot": 34 /* S.c_fun_description_cot */,
    "asin": 35 /* S.c_fun_description_asin */,
    "acos": 36 /* S.c_fun_description_acos */,
    "atan": 37 /* S.c_fun_description_atan */,
    "acot": 38 /* S.c_fun_description_acot */,
    "ln": 39 /* S.c_fun_description_ln */,
    "lg": 40 /* S.c_fun_description_lg */,
    "log": 41 /* S.c_fun_description_log */,
    "exp": 42 /* S.c_fun_description_exp */,
    "√": 43 /* S.c_fun_description_sqrt */,
    "sqrt": 43 /* S.c_fun_description_sqrt */,
    "cubic": 44 /* S.c_fun_description_cubic */,
    "abs": 45 /* S.c_fun_description_abs */,
    "sgn": 46 /* S.c_fun_description_sgn */,
    "eq": 47 /* S.c_fun_description_eq */,
    "le": 48 /* S.c_fun_description_le */,
    "ge": 49 /* S.c_fun_description_ge */,
    "ne": 50 /* S.c_fun_description_ne */,
    "lt": 51 /* S.c_fun_description_lt */,
    "gt": 52 /* S.c_fun_description_gt */,
    "rad": 53 /* S.c_fun_description_rad */,
    "dms": 54 /* S.c_fun_description_dms */,
    "deg": 55 /* S.c_fun_description_deg */,
    "x%": 71 /* S.c_pf_description_percent */,
    "x!": 69 /* S.c_pf_description_factorial */,
    "x!!": 70 /* S.c_pf_description_double_factorial */,
    "x°": 72 /* S.c_pf_description_degree */,
    "mod": 63 /* S.c_op_description_mod */,
    "Σ": 64 /* S.c_op_description_sum */,
    "∏": 65 /* S.c_op_description_product */,
    "∂": 66 /* S.c_op_description_derivative */,
    "∫ab": 68 /* S.c_op_description_integral_ab */,
    "∫": 67 /* S.c_op_description_integral */,
};
const FUNCTION_ARGS = {
    "dms": "(d, m, s)",
    "log": "(base, x)",
    "rad": "(x, y, z)",
    "√n": "(x, n)",
    "ap": "(x, y)",
    "eq": "(x, y)",
    "ge": "(x, y)",
    "gt": "(x, y)",
    "le": "(x, y)",
    "lt": "(x, y)",
    "ne": "(x, y)",
    "x!": "",
    "x!!": "",
    "x%": "",
    "mod": "(x, y)",
    "x°": "",
    "∂": "(f(x), x, x_point, order)",
    "∫": "(f(x), x)",
    "∫ab": "(f(x), x, a, b)",
    "Σ": "(f(i), i, from, to)",
    "∏": "(f(i), i, from, to)",
};
const FUNCTIONS_COMMON = [
    "abs",
    "conjugate",
    "cubic",
    "deg",
    "dms",
    "exp",
    "im",
    "lg",
    "ln",
    "log",
    "rad",
    "re",
    "sgn",
    "√",
    "√3",
    "√4",
    "√n",
];
const FUNCTIONS_TRIG = [
    "sin",
    "cos",
    "tan",
    "cot",
    "asin",
    "acos",
    "atan",
    "acot",
];
const FUNCTIONS_CMP = [
    "ap",
    "eq",
    "ge",
    "gt",
    "le",
    "lt",
    "ne",
];
const FUNCTIONS_HYP = [
    "sinh",
    "cosh",
    "tanh",
    "coth",
    "asinh",
    "acosh",
    "atanh",
    "acoth",
];
const FUNCTIONS_OPS = [
    "x!",
    "x!!",
    "x%",
    "mod",
    "x°",
];
const FUNCTIONS_DERIVATIVE = [
    "∂",
    "∫",
    "∫ab",
];
const FUNCTIONS_OTHER = [
    "Σ",
    "∏",
];
const wrapStyle = {
    style: {
        whiteSpace: "pre-line",
    },
};
const FUNCTION_CATEGORIES = {
    [59 /* S.c_fun_category_common */]: FUNCTIONS_COMMON,
    [56 /* S.c_fun_category_trig */]: FUNCTIONS_TRIG,
    [58 /* S.c_fun_category_comparison */]: FUNCTIONS_CMP,
    [57 /* S.c_fun_category_hyper_trig */]: FUNCTIONS_HYP,
    [60 /* S.c_operators */]: FUNCTIONS_OPS,
    [61 /* S.derivatives */]: FUNCTIONS_DERIVATIVE,
    [62 /* S.other */]: FUNCTIONS_OTHER,
};
const FUNCTION_CATEGORY_ARRAY = [
    59 /* S.c_fun_category_common */,
    56 /* S.c_fun_category_trig */,
    58 /* S.c_fun_category_comparison */,
    57 /* S.c_fun_category_hyper_trig */,
    60 /* S.c_operators */,
    61 /* S.derivatives */,
    62 /* S.other */,
];
let functionPage = 0;
export const CalculatorVariableSelect = memo(({ onVar, exit }) => {
    return (_jsxs(_Fragment, { children: [_jsx(AppBar, { children: _jsxs(Toolbar, { children: [_jsx(IconButton, { size: "large", color: "inherit", edge: "start", onClick: exit, children: _jsx(ArrowBack, {}) }), _jsx(Typography, { variant: "h6", component: "div", children: msgs[23 /* S.cpp_vars_and_constants */] })] }) }), _jsx(ScrollableFilledBox, { children: _jsx(Container, { sx: bgSx, children: _jsxs(List, { children: [_jsx(Toolbar, {}), Object.keys(VARIABLES).map(name => (_jsx(ListItem, { disablePadding: true, children: _jsx(ListItemButton, { onClick: useCallback(() => onVar(name), [onVar]), children: _jsx(ListItemText, { primary: name + " = " + VARIABLE_VALUES[name], secondary: msgs[VARIABLES[name]] }) }) }, name)))] }) }) })] }));
});
export const CalculatorFunctionSelect = memo(({ onFun, exit }) => {
    const [currentPage, setCurrentPage] = useState(functionPage);
    const onFunMap = useMemo(() => ({}), []);
    const mapOnFun = (fun) => {
        let result = onFunMap[fun];
        if (!result) {
            result = () => onFun(fun);
            onFunMap[fun] = result;
        }
        return result;
    };
    functionPage = currentPage;
    return (_jsxs(_Fragment, { children: [_jsxs(AppBar, { children: [_jsxs(Toolbar, { children: [_jsx(IconButton, { size: "large", color: "inherit", edge: "start", onClick: exit, children: _jsx(ArrowBack, {}) }), _jsx(Typography, { variant: "h6", component: "div", children: msgs[30 /* S.c_functions */] })] }), _jsx(Tabs, { value: currentPage, onChange: useCallback((_, newPage) => setCurrentPage(newPage), []), variant: "scrollable", indicatorColor: "secondary", textColor: "inherit", scrollButtons: "auto", allowScrollButtonsMobile: true, children: FUNCTION_CATEGORY_ARRAY.map((res) => (_jsx(Tab, { label: msgs[res] }, res))) })] }), _jsx(ScrollableFilledBox, { children: _jsx(Container, { sx: bgSx, children: _jsxs(List, { children: [_jsx(Toolbar, {}), _jsx(Tabs, {}), FUNCTION_CATEGORIES[FUNCTION_CATEGORY_ARRAY[currentPage]].map((fun) => (_jsx(ListItem, { disablePadding: true, children: _jsx(ListItemButton, { onClick: mapOnFun(fun), children: _jsx(ListItemText, { primary: fun + (FUNCTION_ARGS[fun] === undefined ? "(x)" : FUNCTION_ARGS[fun]), secondary: FUNCTION_DESC[fun] ? msgs[FUNCTION_DESC[fun]] : null, secondaryTypographyProps: wrapStyle }) }) }, fun)))] }) }) })] }));
});
